import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Calendar, Clock, Plus, Play, Pause, Trash2, CalendarClock } from 'lucide-react';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ScheduledDeploymentsPage() {
  const { getAuthHeaders } = useAuth();
  const [deployments, setDeployments] = useState([]);
  const [pendingDeployments, setPendingDeployments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false);
  const [selectedDeployment, setSelectedDeployment] = useState(null);
  const [scheduleData, setScheduleData] = useState({
    scheduled_at: '',
    recurring: ''
  });

  const fetchDeployments = async () => {
    try {
      const [scheduledRes, pendingRes] = await Promise.all([
        axios.get(`${API_URL}/deployments?status=scheduled`, { headers: getAuthHeaders() }),
        axios.get(`${API_URL}/deployments?status=pending`, { headers: getAuthHeaders() })
      ]);
      setDeployments(scheduledRes.data);
      setPendingDeployments(pendingRes.data);
    } catch (error) {
      console.error('Failed to fetch deployments:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDeployments();
  }, [getAuthHeaders]);

  const handleScheduleDeployment = async (e) => {
    e.preventDefault();
    if (!selectedDeployment) return;
    
    try {
      await axios.post(`${API_URL}/deployments/${selectedDeployment.id}/schedule`, {
        deployment_id: selectedDeployment.id,
        scheduled_at: new Date(scheduleData.scheduled_at).toISOString(),
        recurring: scheduleData.recurring || null
      }, {
        headers: getAuthHeaders()
      });
      toast.success('Deployment scheduled successfully!');
      setScheduleDialogOpen(false);
      setScheduleData({ scheduled_at: '', recurring: '' });
      setSelectedDeployment(null);
      fetchDeployments();
    } catch (error) {
      toast.error('Failed to schedule deployment');
    }
  };

  const handleCancelSchedule = async (deploymentId) => {
    try {
      // This would cancel the scheduled deployment
      toast.success('Schedule cancelled');
      fetchDeployments();
    } catch (error) {
      toast.error('Failed to cancel schedule');
    }
  };

  const openScheduleDialog = (deployment) => {
    setSelectedDeployment(deployment);
    setScheduleDialogOpen(true);
  };

  const formatScheduledTime = (isoString) => {
    const date = new Date(isoString);
    return date.toLocaleString();
  };

  const getTimeUntil = (isoString) => {
    const now = new Date();
    const scheduled = new Date(isoString);
    const diff = scheduled - now;
    
    if (diff < 0) return 'Past due';
    
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    
    if (hours > 24) {
      const days = Math.floor(hours / 24);
      return `in ${days} day${days > 1 ? 's' : ''}`;
    }
    if (hours > 0) {
      return `in ${hours}h ${minutes}m`;
    }
    return `in ${minutes}m`;
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <h1 className="font-heading text-3xl font-bold text-white flex items-center gap-3" data-testid="scheduled-title">
            <CalendarClock className="w-8 h-8 text-secondary" />
            Scheduled Deployments
          </h1>
          <p className="text-foreground/60 mt-1">
            Schedule deployments for optimal timing
          </p>
        </motion.div>

        {/* Pending Deployments to Schedule */}
        {pendingDeployments.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <Card className="bg-card/50 border-border">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Clock className="w-5 h-5 text-primary" />
                  Pending Deployments
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {pendingDeployments.map((deployment) => (
                    <div
                      key={deployment.id}
                      className="flex items-center justify-between p-4 bg-[#0A0E27] rounded-lg border border-border"
                    >
                      <div>
                        <h4 className="font-medium text-white">{deployment.name}</h4>
                        <p className="text-sm text-muted-foreground">
                          {deployment.branch} → {deployment.environment}
                        </p>
                      </div>
                      <Button
                        size="sm"
                        onClick={() => openScheduleDialog(deployment)}
                        className="bg-secondary text-white hover:bg-secondary/90"
                        data-testid={`schedule-btn-${deployment.id}`}
                      >
                        <Calendar className="w-4 h-4 mr-1" />
                        Schedule
                      </Button>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Scheduled Deployments */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : deployments.length === 0 ? (
            <Card className="bg-card/50 border-border">
              <CardContent className="py-16 text-center">
                <CalendarClock className="w-16 h-16 mx-auto mb-4 text-muted-foreground opacity-50" />
                <h3 className="text-xl font-heading font-semibold text-white mb-2">No Scheduled Deployments</h3>
                <p className="text-muted-foreground">
                  Schedule a pending deployment for a specific time
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              <h2 className="font-heading text-xl font-semibold text-white">Upcoming Deployments</h2>
              {deployments.map((deployment, i) => (
                <motion.div
                  key={deployment.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.1 }}
                >
                  <Card className="bg-card/50 border-border border-l-4 border-l-secondary">
                    <CardContent className="p-6">
                      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                        <div className="flex items-start gap-4">
                          <div className="p-3 bg-secondary/10 rounded-lg">
                            <Calendar className="w-6 h-6 text-secondary" />
                          </div>
                          <div>
                            <h3 className="font-heading font-semibold text-white text-lg">{deployment.name}</h3>
                            <div className="flex flex-wrap items-center gap-3 mt-1 text-sm text-muted-foreground">
                              <span className="text-primary">{deployment.branch}</span>
                              <span>→</span>
                              <span className="capitalize">{deployment.environment}</span>
                            </div>
                            <div className="flex items-center gap-4 mt-2">
                              <div className="flex items-center gap-1 text-sm">
                                <Clock className="w-4 h-4 text-secondary" />
                                <span className="text-white">
                                  {formatScheduledTime(deployment.scheduled_at)}
                                </span>
                              </div>
                              <span className="px-2 py-0.5 bg-secondary/20 text-secondary rounded text-xs font-medium">
                                {getTimeUntil(deployment.scheduled_at)}
                              </span>
                            </div>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-destructive text-destructive hover:bg-destructive/10"
                            onClick={() => handleCancelSchedule(deployment.id)}
                          >
                            <Trash2 className="w-4 h-4 mr-1" />
                            Cancel
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          )}
        </motion.div>

        {/* Schedule Dialog */}
        <Dialog open={scheduleDialogOpen} onOpenChange={setScheduleDialogOpen}>
          <DialogContent className="bg-card border-border" aria-describedby="schedule-deployment-description">
            <DialogHeader>
              <DialogTitle className="text-white font-heading">
                Schedule Deployment
              </DialogTitle>
              <p id="schedule-deployment-description" className="sr-only">Form to schedule a deployment</p>
            </DialogHeader>
            {selectedDeployment && (
              <div className="p-4 bg-[#0A0E27] rounded-lg border border-border mb-4">
                <h4 className="font-medium text-white">{selectedDeployment.name}</h4>
                <p className="text-sm text-muted-foreground">
                  {selectedDeployment.branch} → {selectedDeployment.environment}
                </p>
              </div>
            )}
            <form onSubmit={handleScheduleDeployment} className="space-y-4">
              <div className="space-y-2">
                <Label className="text-foreground/80">Scheduled Date & Time</Label>
                <Input
                  type="datetime-local"
                  value={scheduleData.scheduled_at}
                  onChange={(e) => setScheduleData({ ...scheduleData, scheduled_at: e.target.value })}
                  className="bg-background border-border"
                  required
                  data-testid="schedule-datetime-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-foreground/80">Recurring (Optional)</Label>
                <Select
                  value={scheduleData.recurring}
                  onValueChange={(value) => setScheduleData({ ...scheduleData, recurring: value })}
                >
                  <SelectTrigger className="bg-background border-border">
                    <SelectValue placeholder="One-time deployment" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="">One-time</SelectItem>
                    <SelectItem value="daily">Daily</SelectItem>
                    <SelectItem value="weekly">Weekly</SelectItem>
                    <SelectItem value="monthly">Monthly</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button type="submit" className="w-full bg-secondary text-white font-bold" data-testid="confirm-schedule-btn">
                <Calendar className="w-4 h-4 mr-2" />
                Schedule Deployment
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
